import { useCallback, useMemo, useState } from 'react';

import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import { CompactClubTable } from '@blocks';
import { isFiniteNumber } from '@core';
import { useTranslate } from '@i18n';
import { useClubCollections, useCreateActivity, useUpdateActivity, useUserClubs } from '@queries';
import {
    Button,
    ClubSelectionTabs,
    Inline,
    ModalBase,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Spinner,
    Stack,
    Text,
    u,
} from '@ui';

import { LINKS } from '../../utils/routes';

type EditClubsModalProps = {
    activityId: number;
    currentClubId?: number;
    isCurrentActivity: boolean;
    onClose: () => void;
};

const handleSaveError = () => {
    toast.error('Failed to save activity');
};

export const EditClubsModal = ({ activityId, currentClubId, isCurrentActivity, onClose }: EditClubsModalProps) => {
    const t = useTranslate();
    const navigate = useNavigate();
    const [selectedClubId, setSelectedClubId] = useState(currentClubId);
    const [selectedDisplayMode, setDisplayMode] = useState<'all' | 'bag' | null>(null);
    const { data: clubCollections = [], isLoading, isError } = useClubCollections();
    const { data: userClubs, isLoading: isLoadingUserClubs } = useUserClubs();
    const displayMode = selectedDisplayMode ?? (userClubs?.length ? 'bag' : 'all');
    const { mutateAsync: createActivity, isPending: isCreating } = useCreateActivity({ onError: handleSaveError });
    const { mutateAsync: updateActivity, isPending: isUpdating } = useUpdateActivity({ onError: handleSaveError });
    const isPending = isCreating || isUpdating;
    const isSelectingClub = isLoading || isLoadingUserClubs || isError || !isFiniteNumber(selectedClubId);
    const putterClubs = clubCollections.find(({ typeName }) => typeName === 'Putter')?.clubs;
    const isPutterChange =
        isCurrentActivity &&
        selectedClubId !== currentClubId &&
        !!putterClubs?.some(({ id }) => id === selectedClubId || id === currentClubId);

    const visibleCollections = useMemo(() => {
        if (displayMode === 'all') return clubCollections;

        return clubCollections.map((collection) => ({
            ...collection,
            clubs: collection.clubs.filter(({ id }) => userClubs?.some((club) => club.id === id)),
        }));
    }, [clubCollections, displayMode, userClubs]);

    const handleDismiss = useCallback(() => {
        if (isPending) return;
        onClose();
    }, [isPending, onClose]);

    const handleCreate = useCallback(async () => {
        if (isPending || isSelectingClub || !isCurrentActivity || !isFiniteNumber(selectedClubId)) return;

        const activity = await createActivity(selectedClubId);
        onClose();
        navigate(LINKS.getActivity(activity.id));
    }, [createActivity, isCurrentActivity, isPending, isSelectingClub, navigate, onClose, selectedClubId]);

    const handleUpdate = useCallback(async () => {
        if (isPending || isSelectingClub || isPutterChange || !isFiniteNumber(selectedClubId)) return;

        await updateActivity({ activityId, clubId: selectedClubId });
        onClose();
    }, [activityId, isPending, isPutterChange, isSelectingClub, onClose, selectedClubId, updateActivity]);

    return (
        <ModalBase open variant="partial" fullPartialWidth fullHeight onDismiss={handleDismiss}>
            <ModalHeader>{t('club_selection.title')}</ModalHeader>
            <ModalBody flexGrow={1} flexShrink={1} py={u(4)} style={{ minHeight: 0, maxHeight: 'none', flexBasis: 0 }}>
                <fieldset disabled={isPending} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
                    <Stack gap={24}>
                        <ClubSelectionTabs displayMode={displayMode} onSelect={setDisplayMode} />
                        {isLoading || isLoadingUserClubs ? (
                            <Spinner size="m" />
                        ) : (
                            <CompactClubTable
                                clubCollections={visibleCollections}
                                displaySelectedClubId={selectedClubId}
                                onClick={setSelectedClubId}
                                wrap
                            />
                        )}
                    </Stack>
                </fieldset>
                {isError && <Text>{t('error_message.no_data_available')}</Text>}
            </ModalBody>
            <ModalFooter
                primary={
                    <Inline flexWrap="wrap">
                        {isCurrentActivity && (
                            <Button
                                variant="primary"
                                isDisabled={isSelectingClub || isPending}
                                isLoading={isCreating}
                                onClick={handleCreate}
                            >
                                {t('tag_selection.create_new')}
                            </Button>
                        )}
                        {!isPutterChange && (
                            <Button
                                variant="primary"
                                isDisabled={isSelectingClub || isPending}
                                isLoading={isUpdating}
                                onClick={handleUpdate}
                            >
                                {t('tag_selection.update_current')}
                            </Button>
                        )}
                    </Inline>
                }
            />
        </ModalBase>
    );
};
